import { CrmFieldService, StandardExternalIds, StandardIds, SystemFieldIds } from '@galaxy/crm/services/field-service';
import { CommonModule } from '@angular/common';
import { Component, computed, Inject, Input, SecurityContext, Signal, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import {
  CampaignService,
  GetterCampaignData,
  RecipientCampaignService,
  RecipientCampaignStruct,
} from '@vendasta/campaigns';
import { ActivityInterface, GetMultiCrmObjectResponseInterface } from '@vendasta/crm';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AVATAR_WIDTH } from '../../../constants';
import { TranslationModule } from '../../../i18n';
import { CrmObjectDisplayService, CrmObjectService } from '../../../shared-services';
import { CrmDependencies, CrmInjectionToken, ObjectType } from '../../../tokens-and-interfaces';
import { CollapsibleSectionComponent } from '../../collapsible-section/collapsible-section.component';

import { ActivitiesTimelineService, ActivityExtended } from '../timeline/timeline.service';
import { GalaxyTooltip } from '@vendasta/galaxy/tooltip';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import {
  EMAIL_STATUS_KEYS_V1,
  EMAIL_TITLE_KEYS_V1,
  getEmailTranslationKey,
} from './email-status-translations';

const HEADER_TRANSLATION_KEYS = [
  'ACTIVITY_TYPES.CAMPAIGN',
  'ACTIVITY_TYPES.YESWARE_CAMPAIGN',
  'ACTIVITY_TYPES.EMAIL',
  'ACTIVITY.EMAIL_TITLE.OUTBOUND',
  'ACTIVITY.EMAIL_TITLE.FORWARDED',
  'ACTIVITY.EMAIL_TITLE.RECEIVED_REPLY',
  'ACTIVITY.EMAIL_TITLE.INBOUND',
  ...Object.values(EMAIL_TITLE_KEYS_V1),
  ...Object.values(EMAIL_STATUS_KEYS_V1),
  'CONTACT.ACTIVITIES.UNKNOWN_NAME',
];

@Component({
  selector: 'crm-activity-email',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.scss'],
  providers: [RxState],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    CollapsibleSectionComponent,
    MatCardModule,
    GalaxyAvatarModule,
    MatIconModule,
    GalaxyPipesModule,
    GalaxyTooltip,
  ],
})
export class CrmActivityEmailComponent {
  private readonly translations = toSignal(this.translateService.stream(HEADER_TRANSLATION_KEYS), {
    initialValue: {} as Record<string, string>,
  });
  unknownNamePlaceholder = computed(() => this.translations()['CONTACT.ACTIVITIES.UNKNOWN_NAME'] ?? '');
  private _activity: WritableSignal<ActivityExtended> = signal({});
  private _associatedObjectId: WritableSignal<string> = signal('');
  private _objectType: WritableSignal<ObjectType> = signal('Contact');

  @Input({ required: true }) set activity(activity: ActivityExtended) {
    this._activity.set(activity);
  }

  get activity() {
    return this._activity();
  }

  @Input() set objectType(objectType: ObjectType) {
    this._objectType.set(objectType);
  }

  get objectType() {
    return this._objectType();
  }

  @Input() set associatedObjectId(crmObjectId: string) {
    this._associatedObjectId.set(crmObjectId);
  }

  get associatedObjectId() {
    return this._associatedObjectId();
  }

  private _showAvatar = true;
  @Input() set showAvatar(showAvatar: boolean) {
    this._showAvatar = showAvatar;
  }

  get showAvatar() {
    return this._showAvatar;
  }

  dateFormat = DateFormat.medium;

  showRelatedActivities = signal<boolean>(false);

  associatedCrmObjectName$ = toObservable(this._activity).pipe(switchMap(() => this.getAssociatedObjectName()));
  associatedCrmObjectName = toSignal(this.associatedCrmObjectName$);

  contactEmails$ = toObservable(this._activity).pipe(switchMap((activity) => this.getContactEmails(activity)));
  contactEmails = toSignal(this.contactEmails$);

  campaignId$ = toObservable(this._activity).pipe(switchMap((activity) => this.getCampaignId(activity)));
  campaignId = toSignal(this.campaignId$);

  campaignName$ = toObservable(this._activity).pipe(switchMap((activity) => this.getCampaign(activity)));
  campaignName = toSignal(this.campaignName$);

  isDeletedCampaign = computed(() => this.campaignName() === null);

  accountGroupId$ = toObservable(this._activity).pipe(switchMap((activity) => this.getAccountGroupId(activity)));
  accountGroupId = toSignal(this.accountGroupId$);

  relatedActivities = computed(() => {
    return this.activitiesTimelineService.getRelatedCampaignActivities(this._activity().crmObjectId);
  });

  cardInfo: Signal<ActivityCardInformation> = computed(() => {
    return {
      emailBody:
        this.sanitizer.sanitize(
          SecurityContext.HTML,
          this.getStringFieldData(this.activity, StandardIds.ActivityEmailBody),
        ) || '',

      emailSubject: this.getStringFieldData(this.activity, StandardIds.ActivityEmailSubject),
      emailStatus: this.getStringFieldData(this.activity, StandardIds.ActivityEmailStatus),
      emailStatusReason: this.getStringFieldData(this.activity, StandardIds.ActivityEmailStatusReason),
      emailDirection: this.getStringFieldData(this.activity, StandardIds.ActivityEmailDirection)?.toLowerCase(),

      emailType: this.getStringFieldData(this.activity, StandardIds.ActivityEmailType),
      emailHeaders: this.getStringFieldData(this.activity, StandardIds.ActivityEmailHeaders) || '',
      sourceName: this.getStringFieldData(this.activity, StandardIds.ActivitySourceName),
      effectiveDate: this.getDateFieldData(this.activity, SystemFieldIds.ActivityEffectiveDate),
      relatedActivities: this.getRelatedActivitiesCardInfo(),
      campaignName: this.getStringFieldData(this.activity, StandardIds.ActivityEmailCampaignName),
    };
  });

  ownerName = computed(() => {
    const owner = this.activity?.ownerUser;
    if (owner) {
      return `${owner?.firstName || ''} ${owner?.lastName || ''}`.trim();
    }
    return this.unknownNamePlaceholder();
  });

  isCampaign = computed(() => {
    const { emailType } = this.cardInfo();
    return emailType === 'marketing_campaign_email';
  });

  isYeswareTrackedEmail = computed(() => {
    const { emailType } = this.cardInfo();
    return emailType === 'yesware_tracked_email';
  });

  isYeswareCampaignEmail = computed(() => {
    const { emailType } = this.cardInfo();
    return emailType === 'yesware_campaign_email';
  });

  headerText = computed(() => {
    const t = this.translations();
    const lookup = (key: string): string => (t[key] ? t[key] + ' ' : '');
    const { emailType, emailDirection, emailStatus } = this.cardInfo();
    const ownerNameText = this.ownerName() + ' ';
    const contactNameText = (this.associatedCrmObjectName() || '') + ' ';

    let baseText = '';
    if (emailType === 'marketing_campaign_email') {
      baseText = lookup('ACTIVITY_TYPES.CAMPAIGN');
    } else if (emailType === 'yesware_campaign_email') {
      baseText = lookup('ACTIVITY_TYPES.YESWARE_CAMPAIGN');
    } else {
      baseText = lookup('ACTIVITY_TYPES.EMAIL');
    }

    let verbText = '';
    if (emailDirection === 'outbound') {
      verbText = lookup('ACTIVITY.EMAIL_TITLE.OUTBOUND');
    } else if (emailDirection === 'forwarded') {
      verbText = lookup('ACTIVITY.EMAIL_TITLE.FORWARDED');
    } else if (emailDirection === 'inbound' && emailStatus === 'received reply') {
      verbText = lookup('ACTIVITY.EMAIL_TITLE.RECEIVED_REPLY');
    } else if (emailDirection === 'inbound') {
      verbText = lookup('ACTIVITY.EMAIL_TITLE.INBOUND');
    } else if (emailStatus) {
      verbText = lookup(getEmailTranslationKey(emailStatus, EMAIL_TITLE_KEYS_V1));
    }

    let ownerVerbText = '';
    let result = '';
    switch (emailType) {
      case 'marketing_campaign_email':
        result = `${baseText}${verbText}${contactNameText}`;
        break;
      case 'yesware_campaign_email':
        ownerVerbText = lookup('ACTIVITY.EMAIL_TITLE.OUTBOUND');
        result = `${baseText}${ownerVerbText}${ownerNameText}${verbText}${contactNameText}`;
        break;
      case 'yesware_tracked_email':
        ownerVerbText = lookup('ACTIVITY.EMAIL_TITLE.OUTBOUND');
        result = `${baseText}${ownerVerbText}${ownerNameText}${verbText}${contactNameText}`;
        break;
      default:
        result = `${baseText}${verbText}${ownerNameText}`;
        break;
    }
    return result;
  });

  campaignLink = computed(() => {
    if (this.useBusinessCenterCampaignLink()) {
      return `/account/location/${this.accountGroupId()}/campaigns/${this.campaignId()}/details`;
    } else {
      return `/marketing/campaign/details/${this.campaignId()}`;
    }
  });

  useBusinessCenterCampaignLink: Signal<boolean | undefined>;

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly fieldService: CrmFieldService,
    private readonly campaignService: CampaignService,
    private readonly recipientCampaignService: RecipientCampaignService,
    private readonly crmObjectService: CrmObjectService,
    private readonly translateService: TranslateService,
    protected readonly activitiesTimelineService: ActivitiesTimelineService,
    private readonly sanitizer: DomSanitizer,
    private readonly objectDisplayService: CrmObjectDisplayService,
  ) {
    this.useBusinessCenterCampaignLink = toSignal(
      config.parentNamespace$.pipe(
        map((parentNamespace) => {
          return parentNamespace !== '';
        }),
      ),
    );
  }

  protected readonly AVATAR_WIDTH = AVATAR_WIDTH;
  protected readonly INNER_AVATAR_WIDTH = AVATAR_WIDTH / 2;

  private getRelatedActivitiesCardInfo() {
    const relatedActivities = this.relatedActivities();
    if (relatedActivities.length === 0) {
      return;
    }
    const relatedActivitiesCardInfo: ActivityCardInformation[] = [];

    for (const activity of relatedActivities) {
      const emailStatus = this.getStringFieldData(activity, StandardIds.ActivityEmailStatus);
      const statusText = this.getStatusText(emailStatus);
      const emailStatusReason = this.getStringFieldData(activity, StandardIds.ActivityEmailStatusReason);
      const effectiveDate = this.getDateFieldData(activity, SystemFieldIds.ActivityEffectiveDate);
      relatedActivitiesCardInfo.push({
        emailStatus: statusText,
        emailStatusReason: emailStatusReason,
        effectiveDate: effectiveDate,
      });
    }

    return relatedActivitiesCardInfo;
  }

  private getStatusText(emailStatus: string): string {
    if (!emailStatus) {
      return '';
    }
    const key = getEmailTranslationKey(emailStatus, EMAIL_STATUS_KEYS_V1);
    return this.translations()[key] ?? '';
  }

  private getStringFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  private getCampaignName(id: string): Observable<string | null> {
    if (id !== '') {
      return this.campaignService.get(id).pipe(
        map((campaignData: GetterCampaignData) => campaignData.name || null),
        catchError((error) => {
          console.warn(`Campaign with ID ${id} not found`, error);
          return of(null);
        }),
      );
    }
    return of('');
  }

  private getDateFieldData(activity: ActivityInterface, fieldId: string): Date | null {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.dateValue ?? null;
  }

  private getCampaignId(activity: ActivityInterface): Observable<string> {
    const sourceName = this.getStringFieldData(activity, StandardIds.ActivitySourceName);
    if (sourceName == 'Marketing Campaigns') {
      const recipientId = this.getStringFieldData(activity, StandardIds.ActivityRecordSourceDrillDown1);
      if (recipientId !== '') {
        return this.recipientCampaignService.getRecipientCampaign(recipientId).pipe(
          map((recipientCampaign: RecipientCampaignStruct) => {
            return recipientCampaign.campaignId;
          }),
        );
      }
    } else {
      const campaignId = this.getStringFieldData(activity, StandardIds.ActivityCampaignID);
      return of(campaignId);
    }
    return of('');
  }

  private getAccountGroupId(activity: ActivityInterface): Observable<string> {
    let recipientId = this.getStringFieldData(activity, StandardIds.ActivityCampaignID);
    const sourceName = this.getStringFieldData(activity, StandardIds.ActivitySourceName);
    if (sourceName == 'Marketing Campaigns') {
      recipientId = this.getStringFieldData(activity, StandardIds.ActivityRecordSourceDrillDown1);
    }
    if (recipientId !== '') {
      return this.recipientCampaignService.getRecipientCampaign(recipientId).pipe(
        map((recipientCampaign: RecipientCampaignStruct) => {
          const parts = recipientCampaign.partnerId.split('/');
          if (parts.length > 1) {
            return parts[1];
          }
          return '';
        }),
      );
    }

    return of('');
  }

  private getCampaign(activity: ActivityInterface): Observable<string | null> {
    const campaignID$ = this.getCampaignId(activity);
    return campaignID$.pipe(
      switchMap((campaignId: string) => {
        return this.getCampaignName(campaignId);
      }),
    );
  }

  getAssociatedObjectName(): Observable<string> {
    const associatedContactID = this._activity()?.associations?.find(
      (association) => association?.crmObjectType === 'Contact',
    )?.crmObjectId;

    if (associatedContactID) {
      return this.getContactName(associatedContactID);
    } else {
      return this.useAssociatedObjectID();
    }
  }

  useAssociatedObjectID(): Observable<string> {
    if (this.objectType === 'Contact') {
      return this.getContactName(this.associatedObjectId);
    }
    if (this.objectType === 'Company') {
      return this.crmObjectService.getMultiObject('Company', [this.associatedObjectId]).pipe(
        map((data: GetMultiCrmObjectResponseInterface) => {
          if (!data?.crmObjects || data.crmObjects.length === 0) {
            return '';
          }
          const crmObject = data.crmObjects[0];
          return this.objectDisplayService.getObjectDisplayName('Company', crmObject);
        }),
      );
    }
    return of('');
  }

  private getContactName(objectID: string): Observable<string> {
    return this.crmObjectService.getMultiObject('Contact', [objectID]).pipe(
      map((data: GetMultiCrmObjectResponseInterface) => {
        if (!data?.crmObjects || data.crmObjects.length === 0) {
          return '';
        }
        const crmObject = data.crmObjects[0];
        return this.objectDisplayService.getObjectDisplayName('Contact', crmObject);
      }),
    );
  }

  private getContactEmails(activity: ActivityInterface): Observable<(string | undefined)[]> {
    const crmObjectIds: string[] = [];
    activity.associations?.forEach((association) => {
      if (association?.crmObjectType === 'Contact' && association?.crmObjectId) {
        crmObjectIds.push(association.crmObjectId);
      }
    });
    if (crmObjectIds.length > 0) {
      const contactDetails$ = this.crmObjectService.getMultiObject('Contact', crmObjectIds);
      return contactDetails$.pipe(
        map((resp) => {
          return resp?.crmObjects
            ? resp?.crmObjects?.map(
                (contact) =>
                  this.fieldService.getFieldValueFromCrmObject(contact, StandardExternalIds.Email)?.stringValue,
              )
            : [];
        }),
      );
    }
    return of([]);
  }

  protected readonly PopoverPositions = PopoverPositions;
}

interface ActivityCardInformation {
  sourceName?: string;
  emailBody?: string;
  emailDirection?: string;
  emailStatus: string;
  emailStatusReason?: string;
  emailSubject?: string;
  emailType?: string;
  emailHeaders?: string;
  campaignName?: string;
  effectiveDate: Date | null;
  relatedActivities?: ActivityCardInformation[];
}
